// One send's tracker: its people, which of them a tile has opened, the search, the page on
// screen and the Excel. Every number and every row comes from the one list the server sends,
// so a number always opens exactly its people.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { downloadTableXlsx, fileSlug, type TableCell } from '../../utils/tableXlsx';
import { bannerAPI, type BannerToken, type BatchTracker, type TrackerPerson } from './bannerApi';
import { figureHead, isFigureTask, isFillIn, noFigureWords, signInWords } from './bannerOptions';
import { tokensAsLabels } from './bannerTokens';

/** The tile a list is opened by, as written in the address (&show=). */
export type TrackerShow = 'all' | 'done' | 'declined' | 'pending' | 'overdue' | 'seen' | 'not_seen' | 'older_app';

const WHO: Record<TrackerShow, (p: TrackerPerson) => boolean> = {
  all: () => true,
  done: (p) => p.status === 'Done',
  declined: (p) => p.status === 'Declined',
  pending: (p) => p.status === 'Pending',
  overdue: (p) => p.status === 'Overdue',
  seen: (p) => p.seen,
  not_seen: (p) => !p.seen,
  older_app: (p) => p.olderApp,
};

// By its own keys only: "in" would take __proto__ for a tile, and the address is anyone's to edit.
export const trackerShowFrom = (value: string | null): TrackerShow =>
  value && Object.prototype.hasOwnProperty.call(WHO, value) ? (value as TrackerShow) : 'all';

/** Does this send have that tile? An address can name one it has not got. The list is tilesFor's. */
const sendHasTile = (t: BatchTracker, show: TrackerShow, counts: Record<TrackerShow, number>): boolean => {
  const consent = t.send.taskType === 'consent';
  if (show === 'all') return true;
  if (show === 'seen') return t.seenTracked && !consent;
  if (show === 'not_seen') return t.seenTracked;
  if (show === 'declined') return consent;
  if (show === 'older_app') return t.needsNewApp === true && (consent || counts.older_app > 0);
  return t.tracksDone && (show !== 'overdue' || counts.overdue > 0);
};

export const PAGE_SIZES = [
  { label: '25 per page', value: 25 },
  { label: '50 per page', value: 50 },
  { label: '100 per page', value: 100 },
];

/** India time, whatever the browser's zone: "30 Oct, 4:22 pm". */
const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/** The whole time, for the Excel: a record has to say the year and the zone. */
const whenInFull = (iso: string): string =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} IST`;

const ANSWER_WORD: Record<string, string> = { agreed: 'Agreed', declined: 'Declined', got_it: 'Done' };

export interface TrackerColumn {
  header: string;
  /** What the cell says, on screen and in the Excel alike. Null: an empty cell, a dash on screen. */
  value: (p: TrackerPerson) => TableCell;
  /** The Excel's cell, where it has to say more than the screen. */
  excel?: (p: TrackerPerson) => TableCell;
  /** Only in the Excel: too long for a row. The row's own control opens it instead. */
  excelOnly?: boolean;
  /** Only on screen: the Excel holds the same thing in columns of its own. */
  screenOnly?: boolean;
  /** The column's tag look, for a cell that reads as a status. */
  tag?: boolean;
  /** Only these of its values get the tag; the rest read as plain text. */
  tagWhen?: (value: TableCell) => boolean;
  /** A second, quieter line on screen; its own column in the Excel. */
  under?: { header: string; value: (p: TrackerPerson) => string | null };
  /** The cell's words say there is nothing here yet, so they read quieter than a value. */
  quiet?: (p: TrackerPerson) => boolean;
}

/**
 * Who it is. One column on screen, as in the mockup and wherever people are named: "Asha Verma
 * (100231)". The Excel keeps name and code apart, so a sheet can be sorted and looked up by either.
 */
const PERSON_COLUMNS: TrackerColumn[] = [
  { header: 'Name (employee code)', screenOnly: true, value: (p) => (p.name ? `${p.name} (${p.empCode})` : p.empCode) },
  { header: 'Name', excelOnly: true, value: (p) => p.name ?? p.empCode },
  { header: 'Employee code', excelOnly: true, value: (p) => p.empCode },
];

/** The tracker's columns, for this kind of send. The screen and the Excel draw from these. */
export const trackerColumns = (t: BatchTracker): TrackerColumn[] => {
  const figure = isFigureTask(t.send.taskType);
  const fillIn = isFillIn(t.send.reachLevel);
  const doneWord = fillIn ? 'Filled in' : figure ? 'Reached' : 'Done';
  const isConsent = t.send.taskType === 'consent';
  // Who can do it only on the new app: which app they were last seen on says who must update.
  const appColumn: TrackerColumn = {
    header: 'App',
    tag: true,
    tagWhen: (value) => value === 'Older app',
    value: (p) => (p.olderApp ? 'Older app' : p.seen ? 'Latest' : null),
  };
  const firstSeen: TrackerColumn = {
    header: 'First seen',
    value: (p) => (!t.seenTracked ? 'Not tracked' : p.firstSeenAt ? when(p.firstSeenAt) : p.seen ? 'Seen' : 'Not seen'),
    excel: (p) => (!t.seenTracked ? 'Not tracked' : p.firstSeenAt ? whenInFull(p.firstSeenAt) : p.seen ? 'Seen' : 'Not seen'),
    quiet: () => !t.seenTracked,
  };
  // Frame 8: a consent reads as its answer, with when it was given.
  if (isConsent) {
    return [
      ...PERSON_COLUMNS,
      { header: 'Store', value: (p) => p.store, under: { header: 'Store code', value: (p) => p.storeCode } },
      {
        header: 'Answer',
        tag: true,
        value: (p) => (p.answer ? ANSWER_WORD[p.answer] : p.status),
      },
      {
        header: 'Answered',
        value: (p) => (p.answeredAt ? when(p.answeredAt) : null),
        excel: (p) => (p.answeredAt ? whenInFull(p.answeredAt) : null),
      },
      firstSeen,
      appColumn,
      ...wordingColumns('Agree words', 'Decline words'),
    ];
  }
  return [
    ...PERSON_COLUMNS,
    { header: 'Store', value: (p) => p.store, under: { header: 'Store code', value: (p) => p.storeCode } },
    // Has a value has no number to reach, so no target to show.
    ...(figure && !fillIn ? [{ header: 'Target', value: (p: TrackerPerson) => p.target }] : []),
    ...(figure
      ? [
          {
            header: figureHead(t.send),
            value: (p: TrackerPerson) => p.figure ?? (t.figuresUnavailable ? null : noFigureWords(t.send)),
            quiet: (p: TrackerPerson) => p.figure === null,
          },
        ]
      : []),
    // Nothing marks it done and nothing says who saw it: there is no status to give.
    ...(t.tracksDone || t.seenTracked
      ? [
          {
            header: 'Status',
            tag: true,
            // No status: nothing to do, or a Read this that reached them only as a plain banner.
            value: (p: TrackerPerson) => (p.status === 'Done' ? doneWord : p.status ?? (p.seen ? 'Seen' : 'Not seen')),
          },
        ]
      : []),
    firstSeen,
    ...(t.tracksDone
      ? [
          {
            header: doneWord,
            value: (p: TrackerPerson) => (p.doneAt ? when(p.doneAt) : null),
            excel: (p: TrackerPerson) => (p.doneAt ? whenInFull(p.doneAt) : null),
          },
          // Only a send that cannot be done on an older app has anything to say here.
          ...(t.needsNewApp ? [appColumn] : []),
        ]
      : []),
    ...(t.takesAnswer ? wordingColumns('Button words', null) : []),
  ];
};

/** What the record holds, for the Excel: the wording as that person had it, and their app. */
const wordingColumns = (primary: string, secondary: string | null): TrackerColumn[] => [
  { header: 'Title shown', excelOnly: true, value: (p) => p.wording?.title ?? null },
  { header: 'Message shown', excelOnly: true, value: (p) => p.wording?.message ?? null },
  { header: primary, excelOnly: true, value: (p) => p.wording?.primaryLabel ?? null },
  ...(secondary ? [{ header: secondary, excelOnly: true, value: (p: TrackerPerson) => p.wording?.secondaryLabel ?? null }] : []),
  { header: 'App version', excelOnly: true, value: (p) => p.appVersion ?? null },
  { header: 'Platform', excelOnly: true, value: (p) => p.platform ?? null },
  {
    header: 'Sign-in',
    excelOnly: true,
    value: (p) => (p.answer ? signInWords(p.callerProof) : null),
  },
];

export function useBannerTracker(batchId: string | null, asked: TrackerShow, tokens: BannerToken[] = []) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const [tracker, setTracker] = useState<BatchTracker | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // There is no such send, so nothing to try again and nothing to download.
  const [notFound, setNotFound] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0].value);

  useEffect(() => {
    if (!batchId) {
      setTracker(null);
      setError(null);
      setNotFound(false);
      setQuery('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    bannerAPI
      .tracker(batchId)
      .then((res) => {
        if (cancelled) return;
        setTracker(res.data?.data ?? null);
        if (!res.data?.data) setError('Could not load who has seen this send.');
      })
      .catch((err: any) => {
        if (cancelled) return;
        const missing = err?.response?.status === 404 || err?.response?.status === 400;
        setNotFound(missing);
        setError(missing ? 'This banner could not be found.' : err?.response?.data?.message || 'Could not load who has seen this send.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [batchId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // The numbers on the tiles: from the same list and the same tests as the rows.
  const counts = useMemo(() => {
    const people = tracker?.people ?? [];
    return Object.fromEntries(
      (Object.keys(WHO) as TrackerShow[]).map((key) => [key, people.filter(WHO[key]).length])
    ) as Record<TrackerShow, number>;
  }, [tracker]);

  // A tile this send has not got opens everyone, rather than an empty list and no tile ringed.
  const show: TrackerShow = !tracker || sendHasTile(tracker, asked, counts) ? asked : 'all';

  // A send on another project is not this project's to read or to pull back.
  const foreignProject = tracker?.send.projectId != null && tracker.send.projectId !== selectedProjectId;

  // What the tile opens, narrowed by the search. This whole list is what the Excel holds.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tracker?.people ?? []).filter(
      (p) =>
        WHO[show](p) &&
        (!q || [p.name, p.empCode, p.store, p.storeCode].some((v) => (v ?? '').toLowerCase().includes(q)))
    );
  }, [tracker, show, query]);

  // Back to the first page whenever the list changes under it.
  useEffect(() => setPage(1), [batchId, show, query, pageSize]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [rows, safePage, pageSize]
  );

  const allColumns = useMemo(() => (tracker ? trackerColumns(tracker) : []), [tracker]);
  const columns = useMemo(() => allColumns.filter((c) => !c.excelOnly), [allColumns]);

  const downloadExcel = async () => {
    if (!tracker || rows.length === 0) return;
    const sheet = allColumns.filter((c) => !c.screenOnly);
    const headers = sheet.flatMap((c) => (c.under ? [c.header, c.under.header] : [c.header]));
    const sheetRows = rows.map((p) =>
      sheet.flatMap((c) => {
        const cell = (c.excel ?? c.value)(p);
        return c.under ? [cell, c.under.value(p)] : [cell];
      })
    );
    await downloadTableXlsx(`${fileSlug(tokensAsLabels(tracker.send.title, tokens)) || 'banner'}_tracker`, {
      name: 'Tracker',
      headers,
      rows: sheetRows,
    });
  };

  return {
    tracker,
    loading,
    error,
    notFound,
    foreignProject,
    show,
    reload,
    query,
    setQuery,
    counts,
    columns,
    rows,
    pageRows,
    safePage,
    totalPages,
    pageSize,
    setPageSize,
    prevPage: () => setPage(Math.max(1, safePage - 1)),
    nextPage: () => setPage(Math.min(totalPages, safePage + 1)),
    downloadExcel,
  };
}

export type BannerTrackerState = ReturnType<typeof useBannerTracker>;
